import { api } from '../utils/api'; // Import the existing global API client
import API_ENDPOINTS from '../config/apiConfig'; // Import API_ENDPOINTS

// Fetches forecast accounts from SQL forecastAccounts table
export const getForecastAccounts = async ({ orgId, locationId, chartOfAccountId }) => {
  try {
    const params = {};
    if (orgId) params.orgId = orgId;
    if (locationId) params.locationId = locationId;
    if (chartOfAccountId) params.chartOfAccountId = chartOfAccountId;

    const response = await api.get(API_ENDPOINTS.FORECAST_ACCOUNTS, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching forecast accounts:', error);
    throw error;
  }
};

// Creates or updates a single forecast account entry (driver type and input mode)
// SQL forecastAccounts table backend handles upsert logic
export const upsertForecastAccount = async (forecastAccount) => {
  try {
    // SQL endpoint expects: orgId, locationId, chartOfAccountId, forecastDriver, inputMode
    const payload = {
      orgId: forecastAccount.orgId,
      locationId: forecastAccount.locationId,
      chartOfAccountId: forecastAccount.chartOfAccountId,
      forecastDriver: forecastAccount.forecastDriver,
      inputMode: forecastAccount.inputMode || 'monthly'
    };
    const response = await api.post(API_ENDPOINTS.FORECAST_ACCOUNTS, payload);
    return response.data;
  } catch (error) {
    console.error('Error creating/updating forecast account:', error);
    throw error;
  }
};

// Updates a single forecast account entry by ID
export const updateForecastAccount = async (forecastAccount) => {
  if (!forecastAccount.id) {
    throw new Error('Forecast account ID is required for update.');
  }
  try {
    const response = await api.put(API_ENDPOINTS.FORECAST_ACCOUNTS, forecastAccount);
    return response.data;
  } catch (error) {
    console.error('Error updating forecast account:', error);
    throw error;
  }
};

// Creates/updates multiple forecast account entries in bulk
export const upsertBulkForecastAccounts = async (forecastAccounts) => {
  try {
    const response = await api.post(API_ENDPOINTS.FORECAST_ACCOUNTS, forecastAccounts);
    return response.data;
  } catch (error) {
    console.error('Error creating/updating bulk forecast accounts:', error);
    throw error;
  }
};

// Deletes a single forecast account entry (soft delete)
export const deleteForecastAccount = async (forecastAccountId) => {
  if (!forecastAccountId) {
    throw new Error('Forecast account ID is required for deletion.');
  }
  try {
    const response = await api.delete(`${API_ENDPOINTS.FORECAST_ACCOUNTS}?id=${forecastAccountId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting forecast account:', error);
    throw error;
  }
};

// Helper function to get forecast account for a specific chart of account
export const getForecastAccountForChartOfAccount = async ({ orgId, locationId, chartOfAccountId }) => {
  try {
    const accounts = await getForecastAccounts({ orgId, locationId, chartOfAccountId });
    // Return the first matching account (should only be one per chartOfAccountId/locationId combination)
    return accounts && accounts.length > 0 ? accounts[0] : null;
  } catch (error) {
    console.error('Error fetching forecast account for chart of account:', error);
    return null;
  }
};

// Create forecastAccountsApi object for compatibility
export const forecastAccountsApi = {
  // Get all forecast accounts (using forecastAccounts endpoint)
  getAll: async (organizationId) => {
    try {
      // Extract the actual ID if an object is passed
      let orgId = organizationId;
      if (typeof organizationId === 'object' && organizationId !== null) {
        // Try common ID properties
        orgId = organizationId.id || organizationId.orgId || organizationId.organizationId;
        if (!orgId) {
          console.warn('Could not extract organization ID from object:', organizationId);
          throw new Error('Invalid organization ID provided');
        }
      }

      if (!orgId) {
        throw new Error('Organization ID is required');
      }

      // Use the forecastAccounts endpoint
      const response = await api.get(API_ENDPOINTS.FORECAST_ACCOUNTS, {
        params: { orgId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching forecast accounts:', error);
      throw error;
    }
  },

  // Get forecast account by ID
  getById: async (id) => {
    try {
      const response = await api.get(`${API_ENDPOINTS.FORECAST_ACCOUNTS}?id=${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching forecast account:', error);
      throw error;
    }
  },

  // Create new forecast account
  create: async (data) => {
    try {
      const response = await api.post(API_ENDPOINTS.FORECAST_ACCOUNTS, data);
      return response.data;
    } catch (error) {
      console.error('Error creating forecast account:', error);
      throw error;
    }
  },

  // Update forecast account
  update: async (id, data) => {
    try {
      const response = await api.put(API_ENDPOINTS.FORECAST_ACCOUNTS, { ...data, id });
      return response.data;
    } catch (error) {
      console.error('Error updating forecast account:', error);
      throw error;
    }
  },

  // Delete forecast account
  delete: async (id) => {
    try {
      const response = await api.delete(`${API_ENDPOINTS.FORECAST_ACCOUNTS}?id=${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting forecast account:', error);
      throw error;
    }
  },

  // Bulk operations
  bulkCreate: async (data) => {
    try {
      const response = await api.post(API_ENDPOINTS.FORECAST_ACCOUNTS, data);
      return response.data;
    } catch (error) {
      console.error('Error bulk creating forecast accounts:', error);
      throw error;
    }
  },

  bulkUpdate: async (data) => {
    try {
      const response = await api.post(API_ENDPOINTS.FORECAST_ACCOUNTS, data);
      return response.data;
    } catch (error) {
      console.error('Error bulk updating forecast accounts:', error);
      throw error;
    }
  }
};

export default forecastAccountsApi;